import {
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReportIcon from '@mui/icons-material/Report';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import PersonIcon from '@mui/icons-material/Person';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import CodeIcon from '@mui/icons-material/Code';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Visibility from '../../../common/components/Visibility.tsx';
import { alpha } from '@mui/material/styles';
import { NavigationRoutes } from '../../router/routes.ts';
import { SideBarMenuProps } from './SideBarMenuProps.ts';
import { Assignment } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useState } from 'react';

const SideBarSuperAdmin = ({ handleMenuClick }: SideBarMenuProps) => {
    const [openMenu, setOpenMenu] = useState<{ [key: string]: boolean }>({
        admin: true, // Keep Administration menu open by default
    });

    const location = useLocation();
    const pathname = location.pathname;

    const isTransactionActive = pathname.startsWith(
        NavigationRoutes.transaction
    );
    const isReportsActive = pathname.startsWith(NavigationRoutes.reports);
    const isDeviationsActive = pathname.startsWith(NavigationRoutes.deviations);

    const isAdminGroupActive = [
        NavigationRoutes.userManagement,
        NavigationRoutes.drugManagement,
        NavigationRoutes.storageManagement,
        NavigationRoutes.labelPrinting,
    ].some((base) => pathname.startsWith(base));
    const isUsersActive = pathname.startsWith(NavigationRoutes.userManagement);
    const isDrugsActive = pathname.startsWith(NavigationRoutes.drugManagement);
    const isStorageActive = pathname.startsWith(
        NavigationRoutes.storageManagement
    );
    const isLabelsActive = pathname.startsWith(NavigationRoutes.labelPrinting);
    const isMessagesActive = pathname.startsWith(NavigationRoutes.messages);
    const isFeedbackActive = pathname.startsWith(NavigationRoutes.feedback);
    const isSuperAdminGroupActive = [
        NavigationRoutes.tenants,
        NavigationRoutes.tenantOrganizations,
        NavigationRoutes.organizationSettings,
        NavigationRoutes.feedback,
    ].some((base) => pathname.startsWith(base));
    const isOrgSettingsActive = pathname.startsWith(
        NavigationRoutes.organizationSettings
    );
    const isTenantsActive = pathname.startsWith(NavigationRoutes.tenants);
    const isTenantOrgsActive = pathname.startsWith(
        NavigationRoutes.tenantOrganizations
    );
    const isProfileActive = pathname.startsWith(NavigationRoutes.profile);
    const isSupportActive = pathname.startsWith(NavigationRoutes.support);
    const isDevThemeActive = pathname.startsWith(NavigationRoutes.devTheme);
    const isDevMessageActive = pathname.startsWith(NavigationRoutes.devMessage);

    const handleToggle = (menu: string) => {
        setOpenMenu((prev) => ({ ...prev, [menu]: !prev[menu] }));
    };

    return (
        <nav>
            <List>
                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isTransactionActive}
                    onClick={() =>
                        handleMenuClick(NavigationRoutes.transaction)
                    }
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Förbrukningsjournal" />
                </ListItemButton>
                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isReportsActive}
                    onClick={() => handleMenuClick(NavigationRoutes.reports)}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <Assignment />
                    </ListItemIcon>
                    <ListItemText primary="Rapporter" />
                </ListItemButton>
                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isDeviationsActive}
                    onClick={() => handleMenuClick(NavigationRoutes.deviations)}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <ReportIcon />
                    </ListItemIcon>
                    <ListItemText primary="Avvikelser" />
                </ListItemButton>

                {/* Messages Section */}
                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isMessagesActive}
                    onClick={() => handleMenuClick(NavigationRoutes.messages)}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <MailOutlineIcon />
                    </ListItemIcon>
                    <ListItemText primary="Meddelanden" />
                </ListItemButton>

                {/* Administration Section */}
                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isAdminGroupActive}
                    onClick={() => handleToggle('admin')}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Administration" />
                    {openMenu['admin'] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openMenu['admin']} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ mt: 0.5 }}>
                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                                mb: 0.5,
                            })}
                            selected={isUsersActive}
                            onClick={() =>
                                handleMenuClick(NavigationRoutes.userManagement)
                            }
                        >
                            <ListItemText
                                inset
                                primary="Användare"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isUsersActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isUsersActive ? 500 : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                                mb: 0.5,
                            })}
                            selected={isDrugsActive}
                            onClick={() =>
                                handleMenuClick(NavigationRoutes.drugManagement)
                            }
                        >
                            <ListItemText
                                inset
                                primary="Läkemedel"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isDrugsActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isDrugsActive ? 500 : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                                mb: 0.5,
                            })}
                            selected={isStorageActive}
                            onClick={() =>
                                handleMenuClick(
                                    NavigationRoutes.storageManagement
                                )
                            }
                        >
                            <ListItemText
                                inset
                                primary="Lagerställe"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isStorageActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isStorageActive ? 500 : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                            })}
                            selected={isLabelsActive}
                            onClick={() =>
                                handleMenuClick(NavigationRoutes.labelPrinting)
                            }
                        >
                            <ListItemText
                                inset
                                primary="Skriv ut QR-koder"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isLabelsActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isLabelsActive ? 500 : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                    </List>
                </Collapse>

                {/* SuperAdmin Section */}
                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isSuperAdminGroupActive}
                    onClick={() => handleToggle('superadmin')}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <SecurityIcon />
                    </ListItemIcon>
                    <ListItemText primary="SuperAdmin" />
                    {openMenu['superadmin'] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse
                    in={openMenu['superadmin']}
                    timeout="auto"
                    unmountOnExit
                >
                    <List component="div" disablePadding sx={{ mt: 0.5 }}>
                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                                mb: 0.5,
                            })}
                            selected={isTenantsActive}
                            onClick={() =>
                                handleMenuClick(NavigationRoutes.tenants)
                            }
                        >
                            <ListItemText
                                inset
                                primary="Tenants"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isTenantsActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isTenantsActive ? 500 : 400,
                                    },
                                }}
                            />
                        </ListItemButton>

                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                                mb: 0.5,
                            })}
                            selected={isTenantOrgsActive}
                            onClick={() =>
                                handleMenuClick(
                                    NavigationRoutes.tenantOrganizations
                                )
                            }
                        >
                            <ListItemText
                                inset
                                primary="Organisationer"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isTenantOrgsActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isTenantOrgsActive
                                            ? 500
                                            : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                            })}
                            selected={isOrgSettingsActive}
                            onClick={() =>
                                handleMenuClick(
                                    NavigationRoutes.organizationSettings
                                )
                            }
                        >
                            <ListItemText
                                inset
                                primary="Inställningar"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isOrgSettingsActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isOrgSettingsActive
                                            ? 500
                                            : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            sx={(theme) => ({
                                ml: 2,
                                pl: 1,
                                py: 0.5,
                                minHeight: 36,
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    borderLeft: '3px solid',
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                    ),
                                },
                                '&:hover': {
                                    backgroundColor: alpha(
                                        theme.palette.action.hover,
                                        0.04
                                    ),
                                },
                            })}
                            selected={isFeedbackActive}
                            onClick={() =>
                                handleMenuClick(NavigationRoutes.feedback)
                            }
                        >
                            <ListItemText
                                inset
                                primary="Feedback"
                                slotProps={{
                                    primary: {
                                        fontSize: '0.875rem',
                                        color: isFeedbackActive
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: isFeedbackActive
                                            ? 500
                                            : 400,
                                    },
                                }}
                            />
                        </ListItemButton>
                    </List>
                </Collapse>

                {/* Profile Section */}
                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isSupportActive}
                    onClick={() => handleMenuClick(NavigationRoutes.support)}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <ContactSupportIcon />
                    </ListItemIcon>
                    <ListItemText primary="Support" />
                </ListItemButton>

                <ListItemButton
                    sx={{ my: 0.5 }}
                    selected={isProfileActive}
                    onClick={() => handleMenuClick(NavigationRoutes.profile)}
                >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                        <PersonIcon />
                    </ListItemIcon>
                    <ListItemText primary="Min profil" />
                </ListItemButton>

                {/* Development Section (Visible in Development Mode) */}
                <Visibility visible={import.meta.env.MODE === 'development'}>
                    <ListItemButton
                        sx={{ my: 0.5 }}
                        selected={isDevThemeActive || isDevMessageActive}
                        onClick={() => handleToggle('dev')}
                    >
                        <ListItemIcon sx={{ minWidth: 34 }}>
                            <CodeIcon />
                        </ListItemIcon>
                        <ListItemText primary="Development" />
                        {openMenu['dev'] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={openMenu['dev']} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ mt: 0.5 }}>
                            <ListItemButton
                                sx={(theme) => ({
                                    ml: 2,
                                    pl: 1,
                                    py: 0.5,
                                    minHeight: 36,
                                    borderRadius: 1,
                                    '&.Mui-selected': {
                                        borderLeft: '3px solid',
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            0.08
                                        ),
                                    },
                                    '&:hover': {
                                        backgroundColor: alpha(
                                            theme.palette.action.hover,
                                            0.04
                                        ),
                                    },
                                    mb: 0.5,
                                })}
                                selected={isDevThemeActive}
                                onClick={() =>
                                    handleMenuClick(NavigationRoutes.devTheme)
                                }
                            >
                                <ListItemText
                                    inset
                                    primary="Themes-page"
                                    slotProps={{
                                        primary: {
                                            fontSize: '0.875rem',
                                            color: isDevThemeActive
                                                ? 'primary.main'
                                                : 'text.secondary',
                                            fontWeight: isDevThemeActive
                                                ? 500
                                                : 400,
                                        },
                                    }}
                                />
                            </ListItemButton>
                            <ListItemButton
                                sx={(theme) => ({
                                    ml: 2,
                                    pl: 1,
                                    py: 0.5,
                                    minHeight: 36,
                                    borderRadius: 1,
                                    '&.Mui-selected': {
                                        borderLeft: '3px solid',
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            0.08
                                        ),
                                    },
                                    '&:hover': {
                                        backgroundColor: alpha(
                                            theme.palette.action.hover,
                                            0.04
                                        ),
                                    },
                                })}
                                selected={isDevMessageActive}
                                onClick={() =>
                                    handleMenuClick(NavigationRoutes.devMessage)
                                }
                            >
                                <ListItemText
                                    inset
                                    primary="Message page"
                                    slotProps={{
                                        primary: {
                                            fontSize: '0.875rem',
                                            color: isDevMessageActive
                                                ? 'primary.main'
                                                : 'text.secondary',
                                            fontWeight: isDevMessageActive
                                                ? 500
                                                : 400,
                                        },
                                    }}
                                />
                            </ListItemButton>
                        </List>
                    </Collapse>
                </Visibility>
            </List>
        </nav>
    );
};

export default SideBarSuperAdmin;
